// Solving a puzzle: the position goes on the board at the puzzle's ply, the
// giveaways are hidden, the reader plays their side through the review's
// own `play`, and the engine answers for the other side. A reader move is
// right while it follows the puzzle's line; if the engine's reply leaves the
// line, the reader's next moves are checked against the engine's own choice
// instead. The outcome goes to `attempts.json`.
import type { Engine } from '@chess/api-engine';
import type { Analysis } from '@chess/api-analysis';
import type { Review, ReviewState } from '@chess/api-review';
import { DEFAULT_MISTAKE_RULES, type San, type Uci } from '@chess/api-model';
import { uciToInput } from '@chess/api-chess';
import type { Puzzle } from './finder.ts';
import { solutionLineOf, type SolutionRules } from './solution-line.ts';

/** `showing` while the solution plays itself on the board, `shown` once it has; neither counts as solved. */
export type SolveStatus = 'playing' | 'thinking' | 'solved' | 'failed' | 'showing' | 'shown';

export interface Solving {
  puzzle: Puzzle;
  status: SolveStatus;
  /** Moves of the line applied so far, both sides. */
  applied: number;
  /** The reader's own moves needed; the puzzle is solved when they are all in. */
  needed: number;
  /** False once the engine's reply left the stored line. */
  onLine: boolean;
  tries: number;
  /** The reader's wrong move, when failed. */
  wrong?: Uci;
  /** Opening puzzle: the step the reader is on. */
  step?: number;
  /**
   * The moves on the board from the puzzle's position, in SAN: the solution's
   * as it shows and once shown; the reader's and the line played out after
   * them once solved.
   */
  solution?: San[];
  /**
   * Solved: the rest of the line playing out after the reader's finding move
   * (`from` moves were on the board when the puzzle was solved; `playing`
   * until the last of the line is on the board). Absent when nothing is
   * played out: the reader's move was not the line's first.
   */
  playOut?: { from: number; playing: boolean };
  /** The move that solved it (the reader's last), set with `solved`; the board marks it. */
  solvedMove?: Uci;
  /** True once the line has played out, or when there was nothing to play out: the clicks are free again (card.ts, `clicksBlocked`). */
  playedOut?: boolean;
}

export interface SolverDeps {
  review: Review;
  engine: Engine;
  analysis: Analysis;
  onChange(s: Solving | undefined): void;
  onResult(puzzle: Puzzle, status: 'solved' | 'failed', tries: number): void;
  /** How long a wrong move stays on the board before it is taken back; 3 s by default. */
  takeBackMs?: number;
  /** The pause between the solution's moves as it plays itself; `SHOW_STEP_MS` by default. */
  showStepMs?: number;
  /** How long the solved position stays still, marked, before the line plays out; `SOLVED_PAUSE_MS` by default. */
  solvedPauseMs?: number;
  /**
   * The rules of chess, to walk the scan's line for Show solution
   * (solution-line.ts); without them only the puzzle's stored line plays.
   */
  chess?: SolutionRules;
}

/** A wrong move stays on the board this long, then is taken back for another try. */
export const TAKE_BACK_MS = 3000;

/** The pause between moves when the solution plays itself. */
export const SHOW_STEP_MS = 600;

/**
 * The beat on the solved position before the line plays out (2026-10-12):
 * long enough for the reader to see that their move was the one, with the
 * board marking it (review-board, on the `review.puzzleSolved` event), and
 * short enough not to feel like a stop.
 */
export const SOLVED_PAUSE_MS = 1200;

/** How deep the engine thinks for a reply and for checking an off-line move. */
const REPLY_DEPTH = 10;

export function readerMovesIn(line: Uci[]): number {
  return Math.ceil(line.length / 2);
}

export function createSolver(deps: SolverDeps) {
  let current: Solving | undefined;
  let unsubscribe: (() => void) | undefined;
  let run = 0;

  const set = (next: Solving | undefined): void => {
    current = next;
    deps.onChange(current);
  };

  /** The board went somewhere the puzzle is not: it ends, neither solved nor failed. */
  const abandon = (): void => {
    run++;
    unsubscribe?.();
    unsubscribe = undefined;
    set(undefined);
  };

  /** The engine's single best move from a position. */
  const bestMove = async (fen: string, signalRun: number): Promise<Uci | undefined> => {
    const release = deps.analysis.hold();
    try {
      await deps.engine.stop();
      let best: Uci | undefined;
      for await (const update of deps.engine.analyse(fen, { depth: REPLY_DEPTH, multiPv: 1 })) {
        if (signalRun !== run) return undefined;
        best = update.lines[0]?.pv[0] ?? best;
      }
      return best;
    } finally {
      release();
    }
  };

  const finish = (status: 'solved' | 'failed', wrong?: Uci): void => {
    if (!current) return;
    const solvedMove = status === 'solved' ? deps.review.state().exploring?.moves.at(-1)?.uci : undefined;
    set({ ...current, status, wrong, solvedMove });
    deps.onResult(current.puzzle, status, current.tries);
    if (status === 'failed') scheduleTakeBack();
    else {
      // The solved position stays still for a beat, marked, before the line goes on.
      const mine = run;
      setTimeout(() => {
        if (mine === run) playOut();
      }, deps.solvedPauseMs ?? SOLVED_PAUSE_MS);
    }
  };

  /**
   * Solved: the line goes on by itself from the position the reader reached,
   * so they see how the move pays off. The remainder is the solution's line
   * (`engineLineOf`, where Show solution would stop too) past the moves on
   * the board; when those are not the line's own (the reader's move was
   * another the engine accepted, or the engine's reply had left the line)
   * nothing more is played. The state stays `solved` throughout, the result
   * recorded once above; the controls are off until the last move and the
   * reader may step back through the line afterwards.
   */
  const playOut = (stepMs = deps.showStepMs ?? SHOW_STEP_MS): void => {
    if (!current || current.status !== 'solved') return;
    const over = (): void => set({ ...current!, playedOut: true });
    if (current.puzzle.prize.kind === 'theory') return over();
    const line = engineLineOf(current.puzzle);
    const onBoard = deps.review.state().exploring?.moves ?? [];
    if (onBoard.length === 0 || onBoard.some((m, i) => m.uci !== line[i])) return over();
    const rest = line.slice(onBoard.length);
    if (rest.length === 0) return over(); // the whole line is on the board already
    const from = onBoard.length;
    set({ ...current, solution: onBoard.map((m) => m.san), playOut: { from, playing: true } });
    const mine = ++run;
    let i = 0;
    const next = (): void => {
      if (mine !== run || !current || current.status !== 'solved') return;
      const done = (): void => set({ ...current!, playOut: { from, playing: false }, playedOut: true });
      if (i >= rest.length) {
        done();
        return;
      }
      deps.review.browse(undefined);
      if (!deps.review.play(uciToInput(rest[i]))) {
        done();
        return;
      }
      i++;
      const solution = deps.review.state().exploring?.moves.map((m) => m.san) ?? current.solution;
      set({ ...current, applied: current.applied + 1, solution });
      setTimeout(next, stepMs);
    };
    setTimeout(next, stepMs);
  };

  /**
   * A wrong move stays on the board for a moment so the reader sees it, then
   * is taken back: the puzzle returns to the position before it, playing
   * again with the next try. Starting, retrying or stopping in the meantime
   * cancels it, and so does the board having moved away from the wrong move.
   */
  const scheduleTakeBack = (): void => {
    const mine = ++run;
    const failedAt = plyOf(deps.review.state());
    setTimeout(() => {
      if (mine !== run || !current || current.status !== 'failed') return;
      // The board left the wrong move (another ply, another game): the puzzle
      // is abandoned rather than left failed, which would keep its board
      // locked and its giveaways hidden.
      if (plyOf(deps.review.state()) !== failedAt) {
        abandon();
        return;
      }
      // The puzzle's own state first, so following the take-back reads it as
      // a step back to the position, not as the reader abandoning the puzzle.
      set({ ...current, status: 'playing', applied: Math.max(0, current.applied - 1), tries: current.tries + 1, wrong: undefined });
      deps.review.browse(undefined);
      deps.review.stepBackward();
    }, deps.takeBackMs ?? TAKE_BACK_MS);
  };

  /**
   * The correct line from the puzzle's position, both sides, as moves the
   * review can play: for an engine puzzle the scan's line from the position
   * as far as the puzzle's reason is realised (solution-line.ts), the stored
   * line when the rules of chess are not given; for the opening puzzle, each
   * step's first answer with the game's own moves between steps.
   */
  const solutionOf = (puzzle: Solving['puzzle']): Array<ReturnType<typeof uciToInput> | string> => {
    if (puzzle.prize.kind !== 'theory') return engineLineOf(puzzle).map(uciToInput);
    const steps = puzzle.prize.steps;
    const moves = deps.review.state().game?.moves ?? [];
    const out: Array<ReturnType<typeof uciToInput> | string> = [];
    for (let i = 0; i < steps.length; i++) {
      if (i > 0) for (let p = steps[i - 1].ply + 1; p < steps[i].ply; p++) if (moves[p]) out.push(uciToInput(moves[p].uci));
      const answer = steps[i].answers[0];
      if (answer) out.push(answer);
    }
    return out;
  };

  /** An engine puzzle's correct line from its position (solution-line.ts), the stored line without the rules of chess. */
  const engineLineOf = (puzzle: Solving['puzzle']): Uci[] => {
    if (!deps.chess) return puzzle.line;
    const scan = deps.review.state().scan;
    const pv = scan?.scan.positions.find((p) => p.ply === puzzle.ply)?.lines[0]?.pv;
    return solutionLineOf(puzzle, pv, scan?.rules ?? DEFAULT_MISTAKE_RULES, deps.chess).moves;
  };

  /** The other side answers: the line's reply while on it, else the engine's choice. */
  const reply = async (state: ReviewState): Promise<void> => {
    if (!current || current.status !== 'playing') return;
    const mine = ++run;
    set({ ...current, status: 'thinking' });
    const fen = state.exploring?.fen ?? state.game?.positions[state.ply];
    if (!fen) return;
    const engineMove = await bestMove(fen, mine);
    if (mine !== run || !current) return;
    const expected = current.onLine ? current.puzzle.line[current.applied] : undefined;
    const move = expected && engineMove === expected ? expected : engineMove ?? expected;
    if (!move) {
      finish('solved');
      return;
    }
    const onLine = current.onLine && move === expected;
    set({ ...current, status: 'playing', applied: current.applied + 1, onLine });
    // The reader may be looking back while the reply is worked out: the reply
    // is shown at the puzzle's position, so the view returns there first.
    deps.review.browse(undefined);
    if (!deps.review.play(uciToInput(move))) finish('solved');
  };

  /** Where the board is: the end of the explored line, or the game's ply. */
  const plyOf = (state: ReviewState): number => (state.exploring ? state.exploring.fromPly + state.exploring.moves.length : state.ply);

  /**
   * The opening puzzle: the reader replays their book moves, the opponent's
   * moves come from the game. A reader move that is the game's own is a plain
   * step of the review; any other book move opens a line, and since the game's
   * continuation no longer applies there, the puzzle ends solved at once.
   */
  const followTheory = (state: ReviewState): void => {
    if (!current || current.status !== 'playing' || current.puzzle.prize.kind !== 'theory') return;
    const steps = current.puzzle.prize.steps;
    const step = current.step ?? 0;
    const at = steps[step]?.ply;
    const game = state.game;
    if (at === undefined || !game) return;
    const ply = plyOf(state);
    // The reader went back, or the board was sent past the reader's move
    // (an address, another ply): abandoned, not failed.
    if (ply < at || ply > at + 1) {
      abandon();
      return;
    }
    if (ply === at) return; // the reader's move is still to come
    const moved = state.exploring ? state.exploring.moves[state.exploring.moves.length - 1] : game.moves[at];
    if (!moved) return;
    if (!steps[step].answers.includes(moved.san)) {
      set({ ...current, applied: current.applied + 1 });
      finish('failed', moved.uci);
      return;
    }
    const next = step + 1;
    set({ ...current, applied: current.applied + 1, step: next });
    if (next >= steps.length || state.exploring) {
      finish('solved');
      return;
    }
    // The opponent's actual moves up to the reader's next turn.
    deps.review.browse(undefined);
    for (let p = at + 1; p < steps[next].ply; p++) {
      const move = game.moves[p];
      if (!move || !deps.review.play(uciToInput(move.uci))) {
        finish('solved');
        return;
      }
    }
  };

  /** Called with every review state; reads the reader's move when there is one. */
  const follow = async (state: ReviewState): Promise<void> => {
    if (!current || (current.status !== 'playing' && current.status !== 'thinking')) return;
    if (current.puzzle.prize.kind === 'theory') {
      followTheory(state);
      return;
    }
    const line = state.exploring;
    if (!line) {
      // The game's own move, played again from the puzzle's position: the
      // review takes it as a plain step forward, not a line, so it comes
      // here as the next ply with no line open. It is the move the puzzle
      // is about (a mistake, or the move that missed the chance), so it is
      // a wrong answer like any other: marked, then taken back (2026-10-13;
      // before, it read as the reader leaving and the puzzle vanished).
      // Stepping forward is held by the constraints while playing, so this
      // ply can only be the reader's move.
      const gameMove = state.game?.moves[current.puzzle.ply];
      if (current.status === 'playing' && current.applied === 0 && state.ply === current.puzzle.ply + 1 && gameMove) {
        set({ ...current, applied: 1 });
        finish('failed', gameMove.uci);
        return;
      }
      // The reader left the line before finishing: the puzzle is abandoned, not failed.
      if (current.applied > 0 || state.ply !== current.puzzle.ply) abandon();
      return;
    }
    // A line from another position is not the puzzle's.
    if (line.fromPly !== current.puzzle.ply) {
      abandon();
      return;
    }
    if (current.status !== 'playing') return;
    if (line.moves.length !== current.applied + 1) return; // not a new reader move
    const readerMove = line.moves[line.moves.length - 1].uci;
    const readerIndex = current.applied; // even indexes are the reader's
    const mine = ++run;
    let right: boolean;
    if (current.onLine) {
      right = readerMove === current.puzzle.line[readerIndex];
    } else {
      set({ ...current, status: 'thinking' });
      const before = line.positions[line.positions.length - 2];
      const best = before ? await bestMove(before, mine) : undefined;
      if (mine !== run || !current) return;
      right = best === undefined || best === readerMove;
    }
    if (!right) {
      set({ ...current, status: 'playing', applied: current.applied + 1 });
      finish('failed', readerMove);
      return;
    }
    const applied = current.applied + 1;
    const readerMovesDone = Math.ceil(applied / 2);
    set({ ...current, status: 'playing', applied });
    if (readerMovesDone >= current.needed) {
      finish('solved');
      return;
    }
    await reply(deps.review.state());
  };

  return {
    current: () => current,
    /** Puts the puzzle on the board and waits for the reader. */
    start(puzzle: Puzzle, tries = 1): void {
      run++;
      unsubscribe?.();
      deps.review.go(puzzle.ply);
      const needed = puzzle.prize.kind === 'theory' ? puzzle.prize.steps.length : readerMovesIn(puzzle.line);
      set({ puzzle, status: 'playing', applied: 0, needed, onLine: true, tries, step: 0, solution: undefined, playOut: undefined });
      unsubscribe = deps.review.subscribe((s) => void follow(s));
    },
    /** Back to the puzzle's position for another go. */
    retry(): void {
      if (!current) return;
      const { puzzle, tries } = current;
      deps.review.returnToGame();
      this.start(puzzle, tries + 1);
    },
    /**
     * Plays the correct line on the board by itself, both sides, one move every
     * `stepMs`, from the puzzle's position: the whole line to where the
     * puzzle's reason is realised (`solutionOf`), its moves so far in
     * `solution` as it unfolds. The reader's moves are not followed meanwhile,
     * and the result is not recorded: seeing the answer is not solving it. Once
     * shown the reader may step back through the line; Try again starts the
     * puzzle afresh from its position.
     */
    showSolution(stepMs = deps.showStepMs ?? SHOW_STEP_MS): void {
      if (!current) return;
      const mine = ++run;
      unsubscribe?.();
      unsubscribe = undefined;
      const moves = solutionOf(current.puzzle);
      deps.review.returnToGame();
      deps.review.go(current.puzzle.ply);
      set({ ...current, status: 'showing', applied: 0, wrong: undefined, onLine: true, solution: [], playOut: undefined });
      let i = 0;
      const next = (): void => {
        if (mine !== run || !current) return;
        if (i >= moves.length) {
          set({ ...current, status: 'shown' });
          return;
        }
        deps.review.browse(undefined);
        if (!deps.review.play(moves[i] as never)) {
          set({ ...current, status: 'shown' });
          return;
        }
        i++;
        // The moves as the review played them, so the SAN is the board's own.
        const solution = deps.review.state().exploring?.moves.map((m) => m.san) ?? current.solution;
        set({ ...current, applied: i, solution });
        setTimeout(next, stepMs);
      };
      setTimeout(next, stepMs);
    },
    /** Ends the puzzle without touching the board: its board was opened afresh or went away. */
    abandon(): void {
      if (current) abandon();
    },
    /** Leaves the puzzle; the board returns to the game. */
    stop(): void {
      run++;
      unsubscribe?.();
      unsubscribe = undefined;
      if (current) deps.review.returnToGame();
      set(undefined);
    },
  };
}
